/**
 *  RaceEngine
 *
 *  Created by Legman on 20/03/2017.
 *
 */

import Point4 from "./Point4";
import RaceCamera from "./RaceCamera";
import RaceCollision from "./RaceCollision";
import RaceEntity from "./RaceEntity";
import RaceSegment from "./RaceSegment";
import RaceTrack from "./RaceTrack";
import RaceView from "./RaceView";

/**
 * @class
 * @memberOf race
 */
export default class RaceEngine {
    /**
     * @param {RaceTrack} track
     * @param {RaceView} view
     */
    constructor(track, view) {
        /**
         * @member {RaceCamera}
         * @private
         */
        this._camera = null;

        /**
         * @member {RaceTrack}
         * @private
         */
        this._track = track;

        /**
         * @member {RaceView}
         * @private
         */
        this._view = view;

        /**
         * @member {Array.<RaceSegment>}
         * @private
         */
        this._trackSegments = [];

        /**
         * @member {Array.<number>}
         * @private
         */
        this._trackGradients = [];

        /**
         * @member {Array}
         * @private
         */
        this._trackCells = [];

        /**
         * @member {Array.<RaceEntity>}
         * @private
         */
        this._entities = [];

        /**
         * @member {Array}
         * @private
         */
        this._tempCache = [];
    }

    init() {
        this._camera = new RaceCamera();

        for (let i = 0; i < this.track.length; ++ i) {
            const data = this.track.textures[i];
            const segment = new RaceSegment(
                RaceEngine.getTexture(data.rM),
                RaceEngine.getTexture(data.rL),
                RaceEngine.getTexture(data.rR),
                RaceEngine.getTexture(data.sL),
                RaceEngine.getTexture(data.sR)
            );
            segment.worldPosition = new Point4(this.track.track[i].x, this.track.track[i].y, this.track.track[i].h);
            segment.distance = i;
            segment.view.visible = false;
            this._view.addChildAt(segment.view, 0);
            this._trackSegments.push(segment);
            this._trackCells.push([]);
        }
        for (let i = 0; i < this._trackSegments.length; ++ i) {
            const segment = this._trackSegments[i];
            segment.next = (i < this._trackSegments.length - 1) ? this._trackSegments[i + 1] : this._trackSegments[0];
            segment.previous = (i > 0) ? this._trackSegments[i - 1] : this._trackSegments[this._trackSegments.length - 1];
        }
        this.calculateTrackGradients();
    }

    /**
     * @param {RaceEntity} entity
     * @param {!number} [distance=0]
     * @param {boolean} [cached=false]
     */
    addEntity(entity, distance = 0, cached = false) {
        if (this._entities.indexOf(entity) !== -1) {
            this.removeEntity(entity);
        }

        entity.engine = this;
        entity.distance = distance;
        entity.worldPosition = this.getPointAt(distance, 0);
        entity.view.visible = !cached;
        this._view.addChild(entity.view);

        entity.init();

        if (cached) {
            this._trackCells[Math.floor(entity.distance)].push(entity);
        } else {
            this.updateTransform(entity);
            this._view.updateRoadEntity(entity);
            this._entities.push(entity);
        }
    }

    /**
     * @param {RaceEntity} entity
     * @param {boolean} [destroy=false]
     */
    removeEntity(entity, destroy = false) {
        for (let i = 0; i < this._entities.length; ++ i) {
            if (this._entities[i] === entity) {
                destroy && entity.destroy();
                this._entities.splice(i, 1);
            }
        }
    }

    /**
     * @param {number} dt
     */
    update(dt) {
        for (let i = 0; i < this._tempCache.length; ++ i) {
            const temp = this._tempCache[i];
            temp.view.visible = false;
        }
        this._tempCache.length = 0;

        // update entities - non cached
        for (let i = this._entities.length - 1; i >= 0; -- i) {
            const entity = this._entities[i];
            if (entity.destroyNextFrame) {
                entity.destroy();
                this._entities.splice(i, 1);
            } else {
                entity.preTransform();
                (entity.type === RaceEntity.TYPE_MOVING) && entity.update(dt);

                // loops distance to allow for laps
                entity.distance %= this.track.length;

                // updates 3d position in world based on distance
                entity.worldPosition = this.getPointAt(entity.distance, 0);
                entity.position = RaceEngine.shiftAndRotate(
                    entity.worldPosition,
                    this._camera.position.x,
                    this._camera.position.y,
                    -this._camera.angle
                );
                entity.screenPosition = this.getProjectionFrom3d(entity.position);
                if (entity.zDepth !== 0) {
                    entity.screenPosition.x += entity.offset.x / entity.zDepth;
                    entity.screenPosition.y += entity.offset.y / entity.zDepth;
                }

                entity.postTransform();
            }
        }

        this.updateCamera(dt);

        const d = this._camera.target ? Math.floor(this._camera.target.distance) : 0;
        let start = d - 1;
        start = (start < 0) ? start + this._track.length : start;
        const end = start + this._view.drawDistance * 2;

        // update cached entities
        for (let i = start; i < end; ++ i) {
            const cell = this._trackCells[i % this._track.length];
            for (let j = 0; j < cell.length; ++ j) {
                const entity = cell[j];
                if (entity.type === RaceEntity.TYPE_MOVING) {
                    entity.update(dt);
                }

                // loops distance to allow for laps
                entity.distance %= this.track.length;

                // updates 3d position in world based on distance
                entity.worldPosition = this.getPointAt(entity.distance, 0);
                entity.position = RaceEngine.shiftAndRotate(
                    entity.worldPosition,
                    this._camera.position.x,
                    this._camera.position.y,
                    -this._camera.angle
                );
                entity.screenPosition = this.getProjectionFrom3d(entity.position);
                if (entity.zDepth !== 0) {
                    entity.screenPosition.x += entity.offset.x / entity.zDepth;
                    entity.screenPosition.y += entity.offset.y / entity.zDepth;
                }
                this._tempCache.push(entity);
            }
        }

        for (let i = start; i < end; ++ i) {
            const segment = this._trackSegments[i % this._track.length];
            segment.position = RaceEngine.shiftAndRotate(
                segment.worldPosition,
                this._camera.position.x,
                this._camera.position.y,
                -this._camera.angle
            );
            segment.screenPosition = this.getProjectionFrom3d(segment.position);
            segment.width = this._view.segmentWidth / segment.zDepth;
            this._tempCache.push(segment);
        }

        // sort z depths
        this.sortDepths();

        // update entity views - cached
        for (let i = 0; i < this._entities.length; ++ i) {
            const entity = this._entities[i];
            this._view.updateRoadEntity(entity);
        }

        // update cached entity views
        for (let i = start; i < end; ++ i) {
            const cell = this._trackCells[i % this._track.length];
            for (let j = 0; j < cell.length; ++ j) {
                const entity = cell[j];
                this._view.updateRoadEntity(entity);
            }
        }

        // update road segment views
        for (let i = start; i < end; ++ i) {
            const segment = this._trackSegments[i % this._track.length];
            const heading = (this._camera.target && this._camera.target.getHeading) ?
                this._camera.target.getHeading() : null;
            this._view.updateRoadSegment(segment, heading);
        }
    }

    /**
     * @param {RaceEntity} entity
     */
    updateTransform(entity) {
        // loops distance to allow for laps
        entity.distance %= this.track.length;

        // updates 3d position in world based on distance
        entity.worldPosition = this.getPointAt(entity.distance, 0);
        entity.position = RaceEngine.shiftAndRotate(
            entity.worldPosition,
            this._camera.position.x,
            this._camera.position.y,
            -this._camera.angle
        );
        entity.screenPosition = this.getProjectionFrom3d(entity.position);
        if (entity.zDepth !== 0) {
            entity.screenPosition.x += entity.offset.x / entity.zDepth;
            entity.screenPosition.y += entity.offset.y / entity.zDepth;
        }
    }

    /**
     * @param {number} dt
     */
    updateCamera(dt) {
        if (!this._camera.target) return;

        const position = this.getPointAt(this._camera.target.distance - this._camera.distance, 0);
        position.z = this._camera.target.worldPosition.z - this._camera.height;
        this._camera.offset.x = -this._camera.target.offset.x / 30;

        this._camera.position.x = position.x;
        this._camera.position.y = position.y;
        this._camera.position.z = position.z;
        this._camera.angle = position.w;

        if (this._camera.shakeDuration > 0) {
            if (this._camera.shakeElapsed < this._camera.shakeDuration) {
                this._camera.shakeElapsed += dt;

                const magnitude = this._camera.shakeMagnitiude;
                this._camera.shake = new PIXI.Point(
                    this._camera.shakeOffset.x + Math.random() * (magnitude - -magnitude) + -magnitude,
                    this._camera.shakeOffset.y + Math.random() * (magnitude - -magnitude) + -magnitude
                );
                this._view.x += (this._camera.shake.x - this._view.x) * this._camera.shakeEase;
                this._view.y += (this._camera.shake.y - this._view.y) * this._camera.shakeEase;
            } else this._camera.shakeDuration = 0;
        }

        const curve = this.getTrackCurvature(this._camera.target.distance);
        const coef = (this._camera.target.velocity ? this._camera.target.velocity : 0) / 4;
        const ease = (curve * this._camera.tilt - this.view.rotation);
        this.view.rotation += (ease * this._camera.tiltEase) * coef;
    }

    sortDepths() {
        const depth = this._entities.concat(this._tempCache);
        depth.sort(function(a, b) {
            return (b.zDepth - b.zDepthOffset) - (a.zDepth - a.zDepthOffset);
        });
        for (let i = depth.length - 1; i >= 0; -- i) {
            this.view.addChildAt(depth[i].view, 0);
        }
    }

    calculateTrackGradients() {
        this._trackGradients = [];

        for (let i = 0; i < this.track.length; ++ i) {
            const h1 = this.track.track[i].h;
            const h2 = (i === this.track.length - 1 ? this.track.track[0].h : this.track.track[i + 1].h);
            this._trackGradients.push(h1 - h2);
        }
    }

    /**
     * @param {!number} l
     * @param {!number} [focus=0]
     * @returns {Point4}
     */
    getPointAt(l, focus = 0) {
        if (l < 0) l += this.track.length;

        let index = Math.floor(l);
        const w2 = l % 1;
        const w1 = 1 - w2;

        let i1 = index;
        let i2 = index + 1;
        i1 %= this.track.length;
        i2 %= this.track.length;

        const w3 = (l + focus) % 1;
        const w4 = 1 - w3;

        index = Math.floor(l + focus);
        let i3 = index;
        let i4 = index + 1;
        i3 %= this.track.length;
        i4 %= this.track.length;

        const p1 = this.track.track[i1];
        const p2 = this.track.track[i2];
        const p3 = this.track.track[i3];
        const p4 = this.track.track[i4];

        return new Point4(
            w1 * p1.x + w2 * p2.x,
            w1 * p1.y + w2 * p2.y,
            w4 * p3.h + w3 * p4.h,
            RaceEngine.angleAverage(p1.a, p2.a, w1, w2)
        );
    }

    /**
     * @param point
     * @returns {PIXI.Point}
     */
    getProjectionFrom3d(point) {
        const z = point.x;
        const y = -point.z * 40;
        const x = point.y;
        const fov = -this._camera.position.z * 40 + this._camera.fieldOfView;
        return new PIXI.Point(
            this._camera.zoom * (x * 20 + this._camera.offset.x) / z + this._camera.vanishingPoint.x,
            -this._camera.zoom * (y - fov) / z + this._camera.vanishingPoint.y
        );
    }

    /**
     * @param {!number} distance
     * @returns {number}
     */
    getTrackCurvature(distance) {
        const a1 = this.track.track[Math.floor(distance) % this.track.length].a;
        const a2 = this.track.track[Math.floor(distance + 1) % this.track.length].a;
        let diff = a2 - a1;
        diff = Math.max(-Math.PI * 2, Math.min(Math.PI * 2, diff));
        if (diff > Math.PI) {
            diff -= Math.PI * 2;
        } else if (diff < -Math.PI) {
            diff += Math.PI * 2;
        }
        return diff;
    }

    /**
     * @param {!number} distance
     * @param {number} [samples=3]
     * @returns {number}
     */
    getAverageTrackCurvature(distance, samples = 3) {
        if (distance < 0) {
            distance += this.track.length;
        }
        let average = 0;
        for (let i = 0; i < samples; ++ i) {
            average += this.getTrackCurvature(distance + i);
        }

        // todo COULD BE THE CAUSE OF THE BUG

        return average / samples;
    }

    /**
     * @param {!number} distance
     * @returns {number}
     */
    getTrackGradient(distance) {
        const g1 = this._trackGradients[Math.floor(distance) % this.track.length];
        const g2 = this._trackGradients[Math.ceil(distance) % this.track.length];
        const w1 = distance % 1;
        const w2 = 1 - w1;
        return w2 * g1 + w1 * g2;
    }

    /**
     * @param {PIXI.Point} p1
     * @param {PIXI.Point} p2
     * @param {PIXI.Point} size1
     * @param {PIXI.Point} size2
     * @returns {RaceCollision}
     */
    testCollision(p1, p2, size1, size2) {
        const w = (size1.x + size2.x) * 0.5;
        const h = size1.y + size2.y;
        const dx = p1.x - p2.x;
        const dy = (p1.y - p2.y) * this._view.segmentHeight;

        let collision;
        if (Math.abs(dx) < w && Math.abs(dy) < h) {
            collision = new RaceCollision();

            const wy = w * dy;
            const hx = h * dx;
            if (wy > hx) {
                if (wy > -hx) collision.top = true;
                else collision.left = true;
            } else {
                if (wy > -hx) collision.right = true;
                else collision.bottom = true;
            }
        }
        return collision;
    }

    /**
     * @static
     * @param {string} url
     * @returns {PIXI.Texture}
     */
    static getTexture(url) {
        return PIXI.Texture.EMPTY;
    }

    /**
     * @static
     * @param {Point4} point
     * @param {number} angle
     */
    static rotateAboutOrigin(point, angle) {
        const x = point.x * Math.cos(angle) - point.y * Math.sin(angle);
        const y = point.y * Math.cos(angle) + point.x * Math.sin(angle);
        point.x = x;
        point.y = y;
        point.w = point.w + angle;
    }

    /**
     * @static
     * @param {Point4} point
     * @param {number} x
     * @param {number} y
     * @param {number} angle
     * @returns {Point4}
     */
    static shiftAndRotate(point, x, y, angle) {
        point = point.clone();
        point.x -= x;
        point.y -= y;
        RaceEngine.rotateAboutOrigin(point, angle);
        return point;
    }

    /**
     * @static
     * @param {number} theta1
     * @param {number} theta2
     * @param {number} w1
     * @param {number} w2
     * @returns {number}
     */
    static angleAverage(theta1, theta2, w1, w2) {
        const x = w1 * Math.cos(theta1) + w2 * Math.cos(theta2);
        const y = w1 * Math.sin(theta1) + w2 * Math.sin(theta2);
        return Math.atan2(y, x);
    }

    /**
     * @returns {RaceCamera}
     */
    get camera() {
        return this._camera;
    }

    /**
     * @returns {RaceTrack}
     */
    get track() {
        return this._track;
    }

    /**
     * @returns {RaceView}
     */
    get view() {
        return this._view;
    }
}
